import React, { useState, useEffect, useRef, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { useForm, FormProvider } from 'react-hook-form';
import { DevTool } from '@hookform/devtools';
import { PaymentIntentResult, SetupIntentResult } from '@stripe/stripe-js';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import ReCAPTCHA from 'react-google-recaptcha';
import { useMst } from '../../../models/stores/Root';
import BillingAddressSection from '../../../components/framework/BillingAddressSection';
import CustomFieldSection from '../../../components/framework/CustomFieldSection';
import GuestContactSection from '../../../components/framework/GuestContactSection';
import GuestShippingSection from '../../../components/framework/GuestShippingSection';
import PaymentMethodSectionWithFormContext from '../../../components/framework/PaymentMethodSectionWithFormContext';
import ShippingMethodSection from '../../../components/framework/ShippingMethodSection';
import ExpressCheckout from '../../../components/input/ExpressCheckout';
import AccountCreateFormWithFormContext from '../../../components/input/AccountCreateFormWithFormContext';
import FormErrorLabel from '../../../components/input/FormErrorLabel';
import Steps from '../../../components/framework/Steps';
import Button from '../../../components/input/Button';
import OrderComment from '../../../components/input/OrderComment';
import { GuestInfoFormData } from '../../../components/input/GuestInfoForm';
import { AddressContract, AddressFormData } from '../../../types/Address';
import ApiValidationError from '../../../models/classes/ApiValidationError';
import ServiceError from '../../../models/classes/ServiceError';
import { CustomerLoginFormData } from '../../../components/input/CustomerLoginForm';
import { SectionSummaryGroupPosition } from '../../../components/summary/SectionSummary';
import { getPaymentSchema } from '../../../models/schemas/Payment';
import UtilityService from '../../../services/UtilityService';
import {
  CartPaymentProfileCardContract,
  CartPaymentProfileContract,
} from '../../../types/PaymentProfile';
import NoPaymentSummary from '../../../components/summary/NoPaymentSummary';
import {
  PayPalAcpHostedFieldsResponseData,
  PayPalAcpMethods,
  PayPalOnApproveData,
  PayPalOnClickData,
} from '../../../types/PayPal';
import {
  APIValues,
  DefaultValues,
  SiteLimitationKeys,
  SpreedlyValues,
  UIConstants,
} from '../../../utils/constants';
import { getPasswordStringValidator } from '../../../models/schemas/Generators';
import { addressSchema } from '../../../models/schemas/Address';
import { getCustomFieldsSchemaWithFormContext } from '../../../models/schemas/CustomFields';
import GiftCardSection from '../../../components/framework/GiftCardSection';
import Spacer from '../../../components/content/Spacer';
import AlertModal from '../../../components/content/AlertModal';
import SpreedlyService from '../../../services/SpreedlyService';
import SpreedlyChallengeModal from '../../../components/content/SpreedlyChallengeModal';
import {
  SpreedlyPostOrderResponse,
  SpreedlyThreeDSStatusEvent,
} from '../../../types/Spreedly';
import {
  getCardBrandFromPaymentMethod,
  getCardExpDateFromPaymentMethod,
  getIntentFromResponse,
  getPaymentIntentIdFromResponse,
  getPaymentMethodIdFromIntent,
  getPaymentMethodObject,
  getSetupIntentIdFromResponse,
} from '../../../utils/stripe/intentParsing';
import PaymentSummary from '../../../components/summary/PaymentSummary';
import { containsCreditCardInfo } from '../OneClickView';

const currentMonth = new Date().getMonth() + 1;

export type GuestPaymentFormData = {
  paymentMethodId: string;
  creditCard: {
    cardNumber: string;
    cardHolderName: string;
    expMonth: string;
    expYear: string;
    cvv: string;
  };
  purchaseOrder?: {
    poNumber: string;
  };
  billingAddressMethodId: string;
  billingAddress: AddressContract;
  createAccount: boolean;
  password: string;
  customFields: {
    [x: string]: string;
  };
  stripe?: string;
};

export type GuestCheckoutViewProps = {
  stripeClientSecretKey?: string;
  onLoginSuccess: () => void;
  onScrollTopOfPage: () => void;
};

const GuestCheckoutView: React.FC<GuestCheckoutViewProps> = observer(
  ({ onLoginSuccess, onScrollTopOfPage }: GuestCheckoutViewProps) => {
    const rootStore = useMst();
    const { configuration, customer, cart, currentStep } = rootStore;
    const reCaptchaRef = useRef<ReCAPTCHA>(null);

    const [userCanLogin, setUserCanLogin] = useState(false);
    const [userMustLogin, setUserMustLogin] = useState(false);

    useEffect(() => {
      setUserCanLogin(
        rootStore.isEmailRegistered &&
          !configuration.features.disableCustomerLogin
      );
    }, [
      configuration.features.disableCustomerLogin,
      rootStore.isEmailRegistered,
    ]);

    useEffect(() => {
      setUserMustLogin(
        !configuration.features.enableGuestCheckout &&
          rootStore.isEmailRegistered &&
          !customer.isLoggedIn
      );
    }, [
      rootStore.isEmailRegistered,
      configuration.features.enableGuestCheckout,
      customer.isLoggedIn,
    ]);

    const [showGiftCardModal, setShowGiftCardModal] = useState<boolean>(false);
    const [clearGiftCard, setClearGiftCard] = useState<boolean>(false);
    const [postingStripeIntent, setPostingStripeIntent] =
      useState<boolean>(false);
    const [captchaResponse, setCaptchaResponse] = useState<string | null>(null);
    const changeCallback = async (response: string | null): Promise<void> => {
      if (configuration.reCaptcha.size === 'invisible') {
        return;
      }
      setCaptchaResponse(null);
      if (response) {
        const result = await configuration.verifyReCaptchaFlow(response);
        if (result === 200) {
          setCaptchaResponse(response);
          cart.updateSubmitToken(response);
        }
      }
    };
    const expiredCallback = () => setCaptchaResponse(null);
    const spreedly3DSChallengeSucceeded = useRef<boolean>(false);

    const applyButtonEnabledRef = useRef<boolean>(false);

    const [hasShippingAddressError, setHasShippingAddressError] =
      useState(false);

    const [checkingIfEmailAddressExists, setCheckingIfEmailAddressExists] =
      useState(false);

    const [hasContactInfoData, setHasContactInfoData] = useState(false);
    const [hasContactInfoError, setHasContactInfoError] = useState(false);
    const [forceEmailError, setForceEmailError] = useState(false);

    const [hasPhoneNumber, setHasPhoneNumber] = useState<boolean>(
      !!cart.customer.phoneNumber
    );

    const [updatedCustomerId, setUpdatedCustomerId] = useState<boolean>(false);

    const [formErrors, setFormErrors] = useState<string[]>([]);

    const [canContinueToPayment, setCanContinueToPayment] = useState(false);

    const isUsingStripe = useMemo(
      () =>
        configuration.isEnabled(SiteLimitationKeys.STRIPE_SERVICE_ENABLED) &&
        configuration.enabledPaymentMethods.filter(method => {
          return method.methodType === 'VolusionPayments';
        }).length >= 1,
      [configuration]
    );

    useEffect(() => {
      if (rootStore.stateMatches('Guest.Shipping')) {
        if (cart.stripeInfo.isWallet && !cart.hasShippingAddress) {
          setFormErrors([
            'The shipping address provided was incomplete. Please review and fill in any missing information.',
          ]);
        }
      }
      if (rootStore.stateMatches('Guest.Payment')) {
        if (cart.stripeInfo.isWallet && !cart.hasBillingAddress) {
          setFormErrors([
            'The billing address provided was incomplete. Please review and fill in any missing information.',
          ]);
        }
      }
    }, [
      cart.stripeInfo.isWallet,
      cart.hasShippingAddress,
      cart.hasBillingAddress,
      rootStore,
      currentStep,
    ]);

    const handleValidGuestContactSection = async (
      formData: GuestInfoFormData
    ): Promise<void> => {
      setForceEmailError(false);
      setCheckingIfEmailAddressExists(true);
      await rootStore.setEmailRegistered(formData.emailAddress);
      cart.updateCustomer(formData);
      setCheckingIfEmailAddressExists(false);
      setHasContactInfoData(true);
    };

    const handleGuestContactSectionErrorStatus = (hasErrors: boolean): void => {
      if (!hasErrors) {
        setForceEmailError(false);
      }
      setHasContactInfoError(hasErrors);
      if (hasErrors && hasContactInfoData) {
        setHasContactInfoData(false);
      }
    };

    const handleShippingAddressSectionErrorStatus = (
      hasErrors: boolean
    ): void => {
      setHasShippingAddressError(hasErrors);
    };

    const handleSubmitWithoutContactData = (): void => {
      setForceEmailError(true);
    };

    const sendContactSavedEvent = (): void => {
      if (
        configuration.features.disableCustomerLogin ||
        !rootStore.isEmailRegistered
      ) {
        rootStore.send({ type: 'CONTACT_SAVED' });
      }
    };

    const handleValidGuestShippingAddress = (
      formData: AddressFormData
    ): void => {
      setFormErrors([]);
      setHasPhoneNumber(!!formData.phoneNumber);

      sendContactSavedEvent();
      if (!cart.billingAddress.hasAnyAddressData) {
        cart.setBillingAddressToShippingValues();
      }
    };

    const handleContinueToPayment = async (): Promise<void> => {
      cart.setUpdatingCart(true);
      await cart.updateCartFlow();
      cart.setUpdatingCart(false);

      if (
        configuration.isEnabled(SiteLimitationKeys.STRIPE_SERVICE_ENABLED) &&
        configuration.hasStripePaymentMethodEnabled
      ) {
        setPostingStripeIntent(true);
        await rootStore.postStripeIntentFlow();
        setPostingStripeIntent(false);
      }

      onScrollTopOfPage();
      sendContactSavedEvent();
      rootStore.send({ type: 'SHIPPING_ADDRESS_SAVED' });
      rootStore.send({ type: 'SHIPPING_METHOD_SELECTED' });
      rootStore.send({ type: 'CONTINUE_TO_PAYMENT' });
    };

    const handleTryCustomerLoginAsync = async (
      formData: CustomerLoginFormData
    ): Promise<string[] | undefined> => {
      try {
        await rootStore.loginFlow(formData);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        if (error.isValidationError && error.errors?.length) {
          return error.errors;
        }
        // eslint-disable-next-line no-console
        console.error(error);
        return ['Unable to login at this time.'];
      }
      return undefined;
    };

    useEffect(() => {
      const errorsToShow =
        hasContactInfoError || hasShippingAddressError
          ? [UIConstants.GENERAL_FORM_ERROR]
          : [];
      setFormErrors(errorsToShow);
    }, [hasContactInfoError, hasShippingAddressError]);

    useEffect(() => {
      setCanContinueToPayment(
        !cart.hasShippingMethodSelected ||
          checkingIfEmailAddressExists ||
          hasShippingAddressError ||
          hasContactInfoError ||
          !hasPhoneNumber ||
          (!configuration.features.disableCustomerLogin &&
            !configuration.features.enableGuestCheckout &&
            rootStore.isEmailRegistered)
      );
    }, [
      cart.hasShippingMethodSelected,
      checkingIfEmailAddressExists,
      hasShippingAddressError,
      hasContactInfoError,
      hasPhoneNumber,
      configuration.features.disableCustomerLogin,
      rootStore.isEmailRegistered,
      configuration.features.enableGuestCheckout,
    ]);

    const handleEditContactInfoClicked = (): void => {
      customer.setPassword(null);
      rootStore.send({ type: 'EDIT_CONTACT' });
    };

    const handleEditShipToClicked = (): void => {
      customer.setPassword(null);
      rootStore.send({ type: 'EDIT_SHIPPING' });
    };

    const handleEditDeliveryClicked = (): void => {
      customer.setPassword(null);
      rootStore.send({ type: 'EDIT_SHIPPING_METHODS' });
    };

    const handleApiValidationError = (error: ApiValidationError): void => {
      setFormErrors(error.extractErrorMessages());
      onScrollTopOfPage();
    };

    const handleGenericError = (error: ServiceError | Error): void => {
      setFormErrors([error.message]);
      onScrollTopOfPage();
    };

    const handleEmailCleared = (): void => {
      rootStore.setEmailRegistered('');
    };

    const isLoginRequired =
      !configuration.features.disableCustomerLogin &&
      !configuration.features.enableGuestCheckout;

    const [displayPaymentMethodAsSummary, setDisplayPaymentMethodAsSummary] =
      useState(false);
    useEffect(() => {
      setDisplayPaymentMethodAsSummary(
        rootStore.stateMatches('Guest.Payment.StripeWallet')
      );
    }, [rootStore, rootStore.currentStep]);

    const [isPaymentState, setIsPaymentState] = useState(false);
    useEffect(() => {
      setIsPaymentState(rootStore.stateMatches('Guest.Payment'));
    }, [rootStore, rootStore.currentStep]);

    const [displayContactSectionAsSummary, setDisplayContactSectionAsSummary] =
      useState(false);
    useEffect(() => {
      setDisplayContactSectionAsSummary(
        hasContactInfoData &&
          !rootStore.stateMatches('Guest.Shipping.Contact.Edit') &&
          !userCanLogin &&
          !userMustLogin
      );
    }, [
      rootStore,
      hasContactInfoData,
      rootStore.currentStep,
      userCanLogin,
      userMustLogin,
    ]);

    const [
      displayShippingAddressAsSummary,
      setDisplayShippingAddressAsSummary,
    ] = useState(false);
    useEffect(() => {
      setDisplayShippingAddressAsSummary(
        isPaymentState ||
          rootStore.stateMatches('Guest.Shipping.Address.Summary')
      );
    }, [rootStore, isPaymentState, rootStore.currentStep]);

    const [displayShippingMethodAsSummary, setDisplayShippingMethodAsSummary] =
      useState(false);
    useEffect(() => {
      setDisplayShippingMethodAsSummary(
        isPaymentState ||
          rootStore.stateMatches('Guest.Shipping.Methods.Summary')
      );
    }, [rootStore, isPaymentState, rootStore.currentStep]);

    const [shippingAddressPosition, setShippingAddressPosition] = useState<
      SectionSummaryGroupPosition | undefined
    >(undefined);
    useEffect(() => {
      switch (true) {
        case displayContactSectionAsSummary && displayShippingMethodAsSummary:
          setShippingAddressPosition('Between');
          break;
        case displayContactSectionAsSummary:
          setShippingAddressPosition('Last');
          break;
        case displayShippingMethodAsSummary:
          setShippingAddressPosition('First');
          break;
        default:
          setShippingAddressPosition(undefined);
      }
    }, [displayContactSectionAsSummary, displayShippingMethodAsSummary]);

    const [shippingMethodsPosition, setShippingMethodsPosition] = useState<
      SectionSummaryGroupPosition | undefined
    >(undefined);
    useEffect(() => {
      switch (true) {
        case displayShippingAddressAsSummary && displayPaymentMethodAsSummary:
          setShippingMethodsPosition('Between');
          break;
        case displayShippingAddressAsSummary:
          setShippingMethodsPosition('Last');
          break;
        case displayPaymentMethodAsSummary:
          setShippingMethodsPosition('First');
          break;
        default:
          setShippingMethodsPosition(undefined);
      }
    }, [displayShippingAddressAsSummary, displayPaymentMethodAsSummary]);

    const passwordSchema = yup.object().shape({
      createAccount: yup.boolean(),
      password: yup.string().when('createAccount', {
        is: (createAccount: boolean) =>
          cart.customer.id === null &&
          (createAccount ||
            (!configuration.features.disableCustomerLogin &&
              !configuration.features.enableGuestCheckout)),
        then: getPasswordStringValidator('password', true),
        otherwise: yup.string().optional(),
      }),
    });

    const billingAddressSchema = yup.object().shape({
      billingAddressMethodId: yup.string().required(),
      billingAddress: yup.object().when('billingAddressMethodId', {
        is: (billingAddressMethodId: string) => billingAddressMethodId === '1',
        then: addressSchema,
        otherwise: yup.object().optional(),
      }),
    });

    const customFieldsSchema = yup.object().shape({
      customFields: getCustomFieldsSchemaWithFormContext(
        cart.cartData.customFields
      ),
    });

    const paymentSchema = getPaymentSchema(
      currentMonth,
      Boolean(configuration.paypalExpress.acpClientToken),
      cart.isPaymentRequired,
      configuration.features.enableSpreedly
    );

    type Theme = 'light' | 'dark';
    type Size = 'compact' | 'normal' | 'invisible';
    type Badge = 'bottomright' | 'bottomleft' | 'inline';

    const paymentViewFormSchema = yup
      .object()
      .concat(paymentSchema)
      .concat(passwordSchema)
      .concat(billingAddressSchema)
      .concat(customFieldsSchema);

    const paymentViewDefaultValues = {
      paymentMethodId: cart.cartData.paymentProfile.id
        ? cart.cartData.paymentProfile.id.toString()
        : configuration.defaultPaymentMethodId,
      createAccount: true,
      billingAddressMethodId: cart.isBillingAddressSameAsShipping ? '0' : '1',
      billingAddress: {
        ...cart.cartData.billingAddress,
        country:
          cart.cartData.billingAddress.country ??
          DefaultValues.DEFAULT_SHIPPING_COUNTRY,
      },
      creditCard: {
        cardNumber: '',
      },
      stripe: '',
    };

    const paymentViewForm = useForm<GuestPaymentFormData>({
      resolver: yupResolver(paymentViewFormSchema),
      shouldUnregister: true,
      defaultValues: paymentViewDefaultValues,
    });

    useEffect(() => {
      if (
        !paymentViewForm.getValues('billingAddress.address1') &&
        cart.billingAddress.address1
      ) {
        paymentViewForm.setValue(
          'billingAddress.firstName',
          cart.billingAddress.firstName
        );
        paymentViewForm.setValue(
          'billingAddress.lastName',
          cart.billingAddress.lastName
        );
        paymentViewForm.setValue(
          'billingAddress.address1',
          cart.billingAddress.address1
        );
        paymentViewForm.setValue(
          'billingAddress.address2',
          cart.billingAddress.address2
        );
        paymentViewForm.setValue(
          'billingAddress.city',
          cart.billingAddress.city
        );
        paymentViewForm.setValue(
          'billingAddress.country',
          cart.billingAddress.country
        );
        paymentViewForm.setValue(
          'billingAddress.state',
          cart.billingAddress.state
        );
        paymentViewForm.setValue(
          'billingAddress.postalCode',
          cart.billingAddress.postalCode
        );
        paymentViewForm.setValue(
          'billingAddress.phoneNumber',
          cart.billingAddress.phoneNumber
        );
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cart.billingAddress.address1, paymentViewForm]);

    const handleOnTokenRequest = async (
      creditCardData: GuestPaymentFormData,
      cardTypeName: string
    ): Promise<string | null> => {
      const { cardNumber, cardHolderName, expMonth, expYear, cvv } =
        creditCardData.creditCard;
      cart.setIsTokenizingCard(true);
      const token = await cart.tokenizeCard({
        cardNumber: cardNumber?.replace(/-|\s/g, ''),
        cardHolderName,
        expireMonth: expMonth,
        expireYear: expYear,
        cvv,
        cardTypeName,
      });
      cart.setIsTokenizingCard(false);
      return token;
    };

    const validateCC = async (
      creditCardData: GuestPaymentFormData
    ): Promise<void> => {
      const cardTypeName = UtilityService.getCreditCardType(
        creditCardData.creditCard.cardNumber
      );
      if (!cardTypeName) {
        paymentViewForm.setError(
          'creditCard.cardNumber',
          {
            type: 'manual',
            message: UIConstants.CARD_NUMBER_INVALID_MESSAGE,
          },
          { shouldFocus: true }
        );
        throw new Error(UIConstants.CARD_NUMBER_INVALID_MESSAGE);
      }
      const matchingMethod = configuration.cardMethods.find(
        method => method.method.toLowerCase() === cardTypeName.toLowerCase()
      );
      if (!matchingMethod) {
        paymentViewForm.setError(
          'creditCard.cardNumber',
          {
            type: 'manual',
            message: UIConstants.CARD_NOT_ACCEPTED_MESSAGE.replace(
              '{0}',
              cardTypeName
            ),
          },
          { shouldFocus: true }
        );
        throw new Error(UIConstants.CARD_NOT_ACCEPTED_MESSAGE);
      }

      const token = await handleOnTokenRequest(creditCardData, cardTypeName);

      if (!token) {
        paymentViewForm.setError(
          'creditCard.cardNumber',
          {
            type: 'manual',
            message: UIConstants.TOKEN_FAILURE_MESSAGE,
          },
          { shouldFocus: true }
        );
        throw new Error(UIConstants.TOKEN_FAILURE_MESSAGE);
      }

      const { cardNumber, cardHolderName, expMonth, expYear, cvv } =
        creditCardData.creditCard;
      const last4 = cardNumber.slice(cardNumber.length - 4);
      const cardConfig: CartPaymentProfileCardContract = {
        cardBrand: null,
        pCIaaSId: token,
        cardNumber: `************${last4}`,
        last4,
        cardType: matchingMethod.id,
        cardTypeName,
        cvv: cvv ?? null,
        holdersName: cardHolderName,
        expMonth: `0${expMonth}`.slice(-2),
        expYear: `20${expYear}`.slice(-4),
        id: null,
        issueMonth: null,
        issueYear: null,
        issueNumber: null,
      };
      cart.updatePaymentProfile(matchingMethod, { cardConfig });
    };

    const validateAndUpdatePaymentProfile = async (
      guestPaymentFormData: GuestPaymentFormData
    ): Promise<boolean> => {
      if (guestPaymentFormData.paymentMethodId === '-1') {
        try {
          await validateCC(guestPaymentFormData);
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error(err);
          cart.setUpdatingCart(false);
          return false;
        }
      } else {
        cart.updatePaymentProfile(
          configuration.getMatchingPaymentMethod(
            guestPaymentFormData.paymentMethodId
          ),
          { poNumber: guestPaymentFormData?.purchaseOrder?.poNumber }
        );
      }
      return true;
    };

    const updateCustomFields = (
      guestPaymentFormData: GuestPaymentFormData
    ): void => {
      if (guestPaymentFormData.customFields) {
        Object.entries(guestPaymentFormData.customFields).forEach(
          ([key, value]) => {
            const fieldNameArr = key.split('_');
            const id = fieldNameArr[fieldNameArr.length - 1];
            cart.updateCustomField(Number(id), value);
          }
        );
      }
    };

    const updateBillingAddress = (
      guestPaymentFormData: GuestPaymentFormData
    ): void => {
      const {
        lastName,
        firstName,
        address1,
        address2,
        city,
        state,
        postalCode,
        country,
        phoneNumber,
        companyName,
      } =
        guestPaymentFormData.billingAddressMethodId === '0'
          ? cart.shippingAddress
          : guestPaymentFormData.billingAddress;

      cart.updateBillingAddress({
        id: null,
        preferred: true,
        lastName: lastName ?? null,
        firstName: firstName ?? null,
        address1: address1 ?? null,
        address2: address2 ?? null,
        city: city ?? null,
        state: state ?? null,
        postalCode: postalCode ?? null,
        country: country ?? null,
        phoneNumber: phoneNumber ?? null,
        companyName: companyName ?? null,
      });
    };

    const handleSpreedlyTransaction = (
      spreedlyTransaction: SpreedlyPostOrderResponse,
      orderId: string
    ) => {
      // Possible status values: 'succeeded', 'failed', 'pending', 'error'
      switch (spreedlyTransaction.status) {
        case null:
          // a null value for status is a success when not cc (we have an orderid at this point)
          rootStore.send({ type: 'ORDER_PLACED', orderId });
          break;
        case SpreedlyValues.SPREEDLY_3DS_SUCCEEDED:
          // Spreedly transaction succeed without needing 3DS authentication!
          rootStore.send({ type: 'ORDER_PLACED', orderId });
          break;
        case SpreedlyValues.SPREEDLY_3DS_PENDING:
          // user will have to authenticate
          SpreedlyService.start3DSLifecycle(
            spreedlyTransaction.transactionToken,
            configuration.payments.spreedlyEnvironmentKey
          );

          rootStore.send({ type: 'SPREEDLY_3DS_LIFECYCLE_STARTED' });
          break;
        default:
          // eslint-disable-next-line no-console
          console.log(`Event ${spreedlyTransaction.status} not handled`);
      }
    };

    const handleUpdatingStripeIntent = async (): Promise<
      PaymentIntentResult | SetupIntentResult | undefined
    > => {
      if (
        !configuration.isEnabled(SiteLimitationKeys.STRIPE_SERVICE_ENABLED) ||
        !configuration.hasStripePaymentMethodEnabled ||
        cart.cartData.paymentProfile.method !== APIValues.STRIPE_METHOD ||
        rootStore.stateMatches('Guest.Payment.StripeWallet')
      ) {
        return undefined;
      }
      const stripeResponse = await rootStore.updateStripeIntentFlow();

      const intent = getIntentFromResponse(stripeResponse);

      if (!intent && stripeResponse?.error) {
        return stripeResponse;
      }

      if (!intent) {
        return undefined;
      }
      const paymentMethod = getPaymentMethodObject(intent);
      const intentCard = paymentMethod?.card;
      const expDateValues = getCardExpDateFromPaymentMethod(paymentMethod);
      cart.updateStripeInfo({
        clientSecretKey: cart.stripeInfo.clientSecretKey,
        paymentIntentId: getPaymentIntentIdFromResponse(stripeResponse),
        paymentMethodId: getPaymentMethodIdFromIntent(intent),
        cardBrand: getCardBrandFromPaymentMethod(paymentMethod),
        last4: intentCard?.last4 ?? null,
        setupIntentId: getSetupIntentIdFromResponse(stripeResponse),
        isSavedCard: false,
        isWallet: false,
        ...expDateValues,
      });

      cart.updatePaymentProfileWithStripeInfo();
      if (
        typeof intentCard?.exp_month !== 'undefined' &&
        typeof intentCard?.exp_year !== 'undefined'
      ) {
        cart.updateCardExpiration(intentCard.exp_month, intentCard?.exp_year);
      }

      return stripeResponse;
    };

    const tryPlaceOrder = async (): Promise<void> => {
      setFormErrors([]);
      if (
        cart.hasRecurringBillingItem &&
        !cart.hasRecurringBillingPaymentMethod
      ) {
        setFormErrors([UIConstants.PROMPT_DIFFERENT_PAYMENT_METHOD]);
        onScrollTopOfPage();
        return;
      }

      let reCaptchaStatus = 400;

      if (
        reCaptchaRef.current &&
        configuration.reCaptcha.checkoutEnabled &&
        configuration.reCaptcha.size === 'invisible'
      ) {
        cart.setUpdatingCart(false);
        cart.updateSubmitToken();
        const reCaptchaValue = await reCaptchaRef.current.executeAsync();
        if (reCaptchaValue) {
          cart.setUpdatingCart(true);
          reCaptchaStatus = await configuration.verifyReCaptchaFlow(
            reCaptchaValue
          );
        }
        reCaptchaRef.current.reset();
        if (reCaptchaStatus !== 200) {
          cart.setUpdatingCart(false);
          setFormErrors([UIConstants.RECAPTCHA_ERROR]);
          onScrollTopOfPage();
          return;
        }
        cart.updateSubmitToken(reCaptchaValue);
      }

      let orderId: string | undefined;
      let spreedlyTransaction;
      let requiresAction: boolean | undefined;

      cart.setUpdatingCart(true);

      try {
        const stripeResponse = await handleUpdatingStripeIntent();

        if (stripeResponse?.error) {
          // Scroll to top of page if the error isn't the error generated from closing the google pay modal
          if (stripeResponse.error?.code !== 'incomplete') {
            onScrollTopOfPage();
          }
          cart.setUpdatingCart(false);
          return;
        }

        customer.setNewCustomerData(cart.cartData);

        if (
          configuration.features.enableSpreedly &&
          !spreedly3DSChallengeSucceeded.current
        ) {
          if (cart.customer.id === null) {
            const customerId = await customer.saveNewCustomer();
            cart.customer.updateId(customerId);
            setUpdatedCustomerId(true);
          }
        }
        if (configuration.features.enableSpreedly) {
          spreedlyTransaction = await cart.spreedlyPlaceOrder();
          orderId = spreedlyTransaction.orderId;
        } else {
          if (cart.customer.id === null) {
            const customerId = await customer.saveNewCustomer();
            cart.customer.updateId(customerId);
            setUpdatedCustomerId(true);
          }

          const orderResponse = await cart.placeOrder();
          orderId = orderResponse.orderId;
          requiresAction = orderResponse.requiresAction;
          if (requiresAction && orderResponse?.transactionToken) {
            cart.updateStripeInfo({
              clientSecretKey: orderResponse.transactionToken,
            });
          }
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        if (error.isValidationError) {
          handleApiValidationError(error);
        } else {
          handleGenericError(error);
        }
      }

      if (orderId) {
        try {
          await customer.saveCustomerAddresses();
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('error', error);
        }
        try {
          if (requiresAction === true) {
            const { error } = await rootStore.handleStripeNextAction(
              cart.stripeInfo.clientSecretKey
            );

            if (error) {
              handleGenericError(error);
              cart.setUpdatingCart(false);
              return;
            }
          }

          const paymentProfileToSave = {
            ...cart.cartData.paymentProfile,
            id: customer.selectedPaymentProfileId ?? -1,
            preferred: true,
          } as CartPaymentProfileContract;

          await customer.saveNewPaymentProfile(paymentProfileToSave);

          if (spreedlyTransaction?.status) {
            handleSpreedlyTransaction(spreedlyTransaction, orderId);
          } else {
            rootStore.send({ type: 'ORDER_PLACED', orderId });
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('error', error);
        }
      } else if (isUsingStripe) {
        cart.updateStripeInfo({ isWallet: false }); // clear isWallet and fetch a fresh intent to allow BNPLs
        await rootStore.postStripeIntentFlow();
      }
      cart.setUpdatingCart(false);
    };

    const handleSpreedlyOnThreeDSStatus = async (
      event: SpreedlyThreeDSStatusEvent
    ) => {
      // Possible event action values: 'succeeded', 'challenge', 'finalization-timeout', 'error'
      switch (event.action) {
        case SpreedlyValues.SPREEDLY_3DS_CHALLENGE:
          // show the challenge-modal
          cart.setShowSpreedlyChallengeModal(true);
          break;
        case SpreedlyValues.SPREEDLY_3DS_SUCCEEDED:
          // finish checkout and redirect to success page
          cart.setShowSpreedlyChallengeModal(false);
          spreedly3DSChallengeSucceeded.current = true;

          cart.updateSpreedlyTransactionDetails({
            transactionToken: event.token,
            challengeResponse: event.action,
          });

          await tryPlaceOrder();
          break;
        default:
          rootStore.send({ type: 'ORDER_ERROR' });
      }
    };

    const handlePaymentMethodChanged = () => setFormErrors([]);

    const handlePayPalAcpOnCardValidated = async (
      data: PayPalAcpHostedFieldsResponseData
    ): Promise<void> => {
      rootStore.setPayPalAcpDetails(data);
      await tryPlaceOrder();
    };

    const onPlaceOrder = async (guestPaymentFormData: GuestPaymentFormData) => {
      setShowGiftCardModal(false);
      setClearGiftCard(false);

      // Check to see if order comments is enabled, and if it contains credit card info
      if (
        configuration?.features?.enableOrderComments &&
        cart?.misc?.comment?.length
      ) {
        const creditCardFound = containsCreditCardInfo(cart.misc.comment);

        if (creditCardFound) {
          setFormErrors([
            'Please remove credit card information from the order comments.',
          ]);
          onScrollTopOfPage();
          return;
        }
      }

      if (applyButtonEnabledRef.current) {
        setShowGiftCardModal(true);
        return;
      }

      customer.resetToGuestDefaults();
      customer.setPassword(guestPaymentFormData.password ?? null);
      updateCustomFields(guestPaymentFormData);
      updateBillingAddress(guestPaymentFormData);

      if (guestPaymentFormData.paymentMethodId === '-1') {
        if (rootStore.payPalAcpOnSubmit) {
          await rootStore.payPalAcpOnSubmit();
          return;
        }
        if (configuration.features.enableSpreedly) {
          cart.setIsTokenizingCard(true);
          rootStore.validateSpreedlyInput();
          return;
        }
      }

      if (!guestPaymentFormData.paymentMethodId) {
        await tryPlaceOrder();
      } else {
        const requiredAction = configuration.payments.getRequired3rdPartyAction(
          guestPaymentFormData.paymentMethodId
        );

        if (requiredAction && !cart.hasRecurringBillingItem) {
          setFormErrors([requiredAction]);
          onScrollTopOfPage();
          return;
        }

        const isPaymentValidated = await validateAndUpdatePaymentProfile(
          guestPaymentFormData
        );

        if (isPaymentValidated) {
          await tryPlaceOrder();
        }
      }
    };

    const payPalButtonMethods = {
      createOrder: rootStore.createPayPalOrder,
      onApprove: async (data: PayPalOnApproveData): Promise<void> => {
        cart.paypalInfo.setIsVenmo(cart.paypalInfo.isVenmoPending);
        rootStore.send({ type: 'PAYPAL_ON_APPROVED', paypalParams: data });
      },
      onClick: async (data: PayPalOnClickData): Promise<void> => {
        cart.paypalInfo.setIsVenmoPending(
          data.fundingSource?.toLowerCase() === 'venmo'
        );
      },
    };

    const handleSpreedlyOnValidation = (
      inputProperties: spreedly.SpreedlyFieldEventInputProperties
    ) => {
      if (inputProperties.validCvv && inputProperties.validNumber) {
        const formValues = paymentViewForm.getValues();
        rootStore.tokenizeSpreedlyCard({
          cardHolderName: formValues.creditCard.cardHolderName,
          expMonth: formValues.creditCard.expMonth,
          expYear: formValues.creditCard.expYear,
        });
        return;
      }
      cart.setIsTokenizingCard(false);
    };

    const handleSpreedlyOnPaymentMethod = async (
      token: string,
      paymentMethod: spreedly.SpreedlyPaymentMethod
    ): Promise<void> => {
      const convertedCardType = SpreedlyService.convertSpreedlyToV1CardType(
        paymentMethod.card_type
      );
      const matchingMethod = configuration.cardMethods.find(
        method =>
          method.method.toLowerCase() === convertedCardType.toLowerCase()
      );
      if (!matchingMethod) {
        setFormErrors([
          UIConstants.CARD_NOT_ACCEPTED_MESSAGE.replace(
            '{0}',
            convertedCardType
          ),
        ]);
        onScrollTopOfPage();
        cart.setIsTokenizingCard(false);
        return;
      }
      const formValues = paymentViewForm.getValues();
      const last4 = paymentMethod.last_four_digits;
      const cardConfig: CartPaymentProfileCardContract = {
        cardBrand: null,
        pCIaaSId: null,
        spreedlyPaymentToken: token,
        paymentMethodFingerprint: paymentMethod.fingerprint,
        cardNumber: `************${last4}`,
        last4,
        cardType: matchingMethod.id,
        cardTypeName: matchingMethod.method,
        cvv: null,
        holdersName: paymentMethod.full_name,
        expMonth:
          paymentMethod.month?.toString()?.padStart(2, '0') ??
          formValues.creditCard.expMonth,
        expYear:
          paymentMethod.year?.toString() ?? formValues.creditCard.expYear,
        id: null,
        issueMonth: null,
        issueYear: null,
        issueNumber: null,
        browserInfo:
          configuration.payments.isSpreedly3DSEnabled &&
          configuration.features.enableSpreedly
            ? // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              Spreedly.ThreeDS.serialize('03', configuration.headerAccept)
            : null,
        transactionToken: null,
        challengeResponse: null,
      };
      cart.updatePaymentProfile(matchingMethod, { cardConfig });

      cart.setIsTokenizingCard(false);
      await tryPlaceOrder();
    };

    const handleSpreedlyOnErrors = (errors: spreedly.SpreedlyError[]): void => {
      if (errors.length) {
        const errorList = errors.map(error => error.message);
        setFormErrors([UIConstants.TOKEN_FAILURE_MESSAGE, ...errorList]);
      }
      cart.setIsTokenizingCard(false);
      onScrollTopOfPage();
    };

    const spreedlyEventHandlers = configuration.features.enableSpreedly
      ? {
          onValidation: handleSpreedlyOnValidation,
          onPaymentMethod: handleSpreedlyOnPaymentMethod,
          onErrors: handleSpreedlyOnErrors,
          onThreeDSStatus: handleSpreedlyOnThreeDSStatus,
        }
      : undefined;

    const acpSetupMethods: PayPalAcpMethods | undefined =
      cart.isUsingPayPalAcp &&
      configuration.paypalExpress.showCreditDebitCardLogos
        ? {
            onCardValidated: handlePayPalAcpOnCardValidated,
          }
        : undefined;

    const amazonPayProps = {
      clientId: configuration.amazonPay.clientId,
      sellerId: configuration.amazonPay.sellerId,
      isSandbox: configuration.amazonPay.isSandbox,
      isLoaded: rootStore.isAmazonPayLoaded,
      onLoaded: (): void => rootStore.setAmazonLoaded(true),
      onAuthorize: async (accessToken: string): Promise<void> => {
        rootStore.send({
          type: 'AMAZONPAY_ON_AUTHORIZE',
          amazonPayToken: accessToken,
        });
      },
    };

    const setApplyButtonEnabled = (enabled: boolean) => {
      applyButtonEnabledRef.current = enabled;
    };

    const handleGiftCardModalContinueButton = () => {
      setClearGiftCard(true);
      paymentViewForm.handleSubmit(onPlaceOrder)();
    };

    useEffect(() => {
      setHasContactInfoData(!!cart.customer.emailAddress);
    }, [cart.customer.emailAddress]);

    useEffect(() => {
      if (
        paymentViewForm.formState.errors.password?.message &&
        rootStore.isEmailRegistered
      ) {
        onScrollTopOfPage();
        paymentViewForm.clearErrors('password');
      }
    }, [
      paymentViewForm.formState.errors.password?.message,
      rootStore.isEmailRegistered,
      onScrollTopOfPage,
      paymentViewForm,
    ]);

    const renderPaymentMethodSection = (
      showSummary: boolean
    ): React.ReactNode => {
      if (showSummary) {
        const { ref, onChange, name } =
          paymentViewForm.register('paymentMethodId');
        return (
          <>
            <input
              type="hidden"
              id="paymentMethodid"
              name={name}
              ref={ref}
              onChange={onChange}
              value={
                configuration.payments.enabledPaymentMethods.find(
                  method => method.methodType === APIValues.STRIPE_METHOD
                )?.id
              }
            />
            <PaymentSummary
              paymentProfile={cart.cartData.paymentProfile}
              onPaymentEdit={() => rootStore.send({ type: 'EDIT_PAYMENT' })}
              appliedGiftCards={cart.cartData.giftCards}
              groupPosition="Last"
            />
          </>
        );
      }

      if (rootStore.stateMatches('Guest.Payment.StripeWallet')) {
        // fix to prevent PayPal from exploding if it doesn't get to render fully
        return null;
      }

      return (
        <>
          <Spacer />
          <PaymentMethodSectionWithFormContext
            onPaymentMethodChanged={handlePaymentMethodChanged}
            hasRecurringBillingItem={cart.hasRecurringBillingItem}
            amazonPayButtonProps={amazonPayProps}
            customPayMessage={configuration.messages.pay}
            paymentMethods={configuration.enabledPaymentMethodsWithFormContext}
            payPalButtonMethods={payPalButtonMethods}
            payPalAcpMethods={acpSetupMethods}
            spreedlyEventHandlers={spreedlyEventHandlers}
          />
        </>
      );
    };

    return (
      <>
        <Steps
          steps={rootStore.breadcrumbs.steps}
          currentStep={rootStore.breadcrumbs.currentStep}
        />
        {rootStore.stateMatches('Guest.Shipping.Address.Add') &&
          cart.isPaymentRequired &&
          !cart.hasRecurringBillingItem &&
          !cart.stripeInfo.paymentIntentId &&
          configuration.features.enableExpressCheckoutDisplay && (
            <ExpressCheckout />
          )}
        <FormErrorLabel errors={formErrors} />
        <GuestContactSection
          onValidData={handleValidGuestContactSection}
          onErrorStatus={handleGuestContactSectionErrorStatus}
          defaultEmailAddress={cart.customer.emailAddress ?? undefined}
          isEmailRegistered={
            !configuration.features.disableCustomerLogin &&
            rootStore.isEmailRegistered
          }
          tryCustomerLoginAsync={
            !configuration.features.disableCustomerLogin
              ? handleTryCustomerLoginAsync
              : undefined
          }
          displaySummary={displayContactSectionAsSummary}
          onContactInfoEdit={handleEditContactInfoClicked}
          onLoginSuccess={onLoginSuccess}
          groupPosition="First"
          loginRequired={isLoginRequired}
          onEmailCleared={handleEmailCleared}
          forceEmailError={forceEmailError}
        />
        <GuestShippingSection
          onErrorStatus={handleShippingAddressSectionErrorStatus}
          onValidData={handleValidGuestShippingAddress}
          displaySummary={displayShippingAddressAsSummary}
          onShipToInfoEdit={handleEditShipToClicked}
          groupPosition={shippingAddressPosition}
          canContinue={hasContactInfoData}
          onMissingContactData={handleSubmitWithoutContactData}
          isBusy={checkingIfEmailAddressExists}
        />
        {!rootStore.stateMatches('Guest.Shipping.Address.Edit') && (
          <ShippingMethodSection
            displaySummary={displayShippingMethodAsSummary}
            onShippingMethodEdit={handleEditDeliveryClicked}
            groupPosition={shippingMethodsPosition}
          />
        )}
        {!rootStore.currentStep.includes('Guest.Payment') ? (
          !rootStore.stateMatches('Guest.Shipping.Address.Add') &&
          !rootStore.stateMatches('Guest.Shipping.Address.Edit') && (
            <Button
              center
              className="max-w-sm"
              isDisabled={canContinueToPayment}
              isOtherActionPending={
                cart.updatingCart ||
                postingStripeIntent ||
                checkingIfEmailAddressExists
              }
              onClick={handleContinueToPayment}
            >
              Continue to Payment
            </Button>
          )
        ) : (
          <FormProvider {...paymentViewForm}>
            <DevTool control={paymentViewForm.control} placement="top-right" />
            <form onSubmit={paymentViewForm.handleSubmit(onPlaceOrder)}>
              {showGiftCardModal && (
                <AlertModal
                  handleContinueButton={handleGiftCardModalContinueButton}
                  headerText="Gift Card Not Applied"
                  bodyText="You’ve entered a gift card without applying it. Are you
                  sure you want to continue?"
                />
              )}
              {configuration.features.enableSpreedly && (
                <SpreedlyChallengeModal
                  showModal={cart.showSpreedlyChallengeModal}
                />
              )}
              {cart.isPaymentRequired ? (
                renderPaymentMethodSection(displayPaymentMethodAsSummary)
              ) : (
                <>
                  <NoPaymentSummary groupPosition="Last" />
                  <Spacer />
                </>
              )}
              {configuration.features.enableGiftCards && (
                <GiftCardSection
                  onGiftCardsUpdating={() => setFormErrors([])}
                  setApplyButtonEnabled={setApplyButtonEnabled}
                  clearGiftCard={clearGiftCard}
                />
              )}
              <BillingAddressSection
                countryOptions={configuration.countryAndStateOptions}
                startWithFormOpen={!cart.isBillingAddressSameAsShipping}
              />
              <CustomFieldSection
                customFields={cart.cartData.customFields}
                useContext
              />
              {!configuration?.features?.disableCustomerLogin &&
                !rootStore.isEmailRegistered &&
                !updatedCustomerId && (
                  <AccountCreateFormWithFormContext
                    needAccountToCheckout={
                      !configuration.features?.enableGuestCheckout
                    }
                    isEmailRegistered={rootStore.isEmailRegistered}
                  />
                )}
              {configuration?.features?.enableOrderComments && (
                <OrderComment
                  orderComment={cart.misc?.comment || ''}
                  onOrderCommentUpdated={cart.updateComment}
                />
              )}
              {configuration?.reCaptcha?.checkoutEnabled && (
                <div className="flex items-center justify-center">
                  <ReCAPTCHA
                    ref={reCaptchaRef}
                    sitekey={configuration?.reCaptcha?.siteKey as string}
                    size={configuration?.reCaptcha?.size as Size}
                    hl={configuration?.reCaptcha?.hl as string}
                    theme={configuration?.reCaptcha?.theme as Theme}
                    badge={configuration?.reCaptcha?.badge as Badge}
                    onExpired={expiredCallback}
                    onChange={changeCallback}
                  />
                </div>
              )}
              <Button
                center
                className="max-w-sm"
                isDisabled={
                  configuration?.reCaptcha?.checkoutEnabled &&
                  configuration?.reCaptcha?.size !== 'invisible' &&
                  !captchaResponse
                }
                isOtherActionPending={
                  cart.isTokenizingCard ||
                  cart.updatingCart ||
                  rootStore.stateMatches('Guest.Payment.ThreeDSPending') ||
                  checkingIfEmailAddressExists
                }
              >
                {cart.stripeInfo.isWallet
                  ? 'Complete Order'
                  : `Pay ${
                      configuration.formattedCurrencySymbol
                    }${cart.grandTotal.toFixed(2)}`}
              </Button>
            </form>
          </FormProvider>
        )}
      </>
    );
  }
);

export default GuestCheckoutView;
